// The submission flow's view of the app-wide country cache.
//
// GET References/country/code resolves the same answer for the whole visit, and
// the flow asks for it on the boost page, on casting calls and again on Step 1 —
// where it decides which promise Step 1 makes ("free for US talent" vs "free for
// your first 30 days"). So it is read from storage rather than re-fetched.
//
// It reuses the SAME localStorage variable the rest of signup uses
// (`CountryCode` / `RegionCode`, via helpers/countryStorage) rather than keeping
// a submission-only copy. That cuts both ways, which is the point: a visitor who
// came through the actor signup first arrives here with the country already
// known, and a visitor who starts on a boost link spares the actor signup its
// own lookup. One fact, one variable, one lookup per device.
//
// Note the shared variable holds country and region only — there is nowhere to
// put `cityId`/`cityName`, and the real endpoint does not return them anyway
// (docs/14 lists a city-level variant as still wanted). A cached hit therefore
// resolves the country and leaves the city to be asked for, exactly as a live
// call against the real API does today. Redux keeps the richer object for the
// rest of the SPA session.

import {
  getCountryCode,
  getRegionCode,
  setCountryCode,
  setRegionCode,
} from "../../../helpers/countryStorage";
import { ISubmissionGeoGuessDto } from "../../../shared/api/submission/types";

export const readCachedGeo = (): ISubmissionGeoGuessDto | null => {
  try {
    const countryCode = getCountryCode();
    if (!countryCode) return null;
    const regionCode = getRegionCode();
    return {
      countryCode: countryCode.toUpperCase(),
      regionCode: regionCode ? regionCode.toUpperCase() : undefined,
    };
  } catch (e) {
    // Private-mode Safari and "block site data" throw on read, not just write.
    // A miss is always safe: the caller re-fetches.
    return null;
  }
};

export const writeCachedGeo = (geo: ISubmissionGeoGuessDto): void => {
  // Never store a countryless answer: it would pin every funnel's country to
  // the empty string on the strength of one failed lookup.
  if (!geo || !geo.countryCode) return;
  try {
    const countryCode = geo.countryCode.toUpperCase();
    const previous = getCountryCode();
    setCountryCode(countryCode);
    if (geo.regionCode) {
      setRegionCode(geo.regionCode.toUpperCase());
    } else if (previous && previous.toUpperCase() !== countryCode) {
      // The country changed and this answer carries no region (a forced
      // ?ctry=, or the country-only response from the real endpoint). Leaving
      // the old one would pair region "BC" with country "US", which
      // ActorPaymentStep and StripeActorPaymentWrapper both read.
      setRegionCode("");
    }
  } catch (e) {
    // Quota or a storage-blocked browser — the flow works without the cache.
  }
};
